import { useCallback, useMemo, useRef, useState } from "react";
import {
  type Fee,
  type FeeType,
  isFeeType
} from "@dealer-center/core-feemanager-interface";
import type { FeeWithUIState } from "../types";
import { validateFeeName } from "../utils/format";

// Drops fees of any retired type (e.g. "ALL"); only NEW and USED are managed.
const withKnownTypes = (fees: Fee[]): Fee[] =>
  fees.filter(fee => isFeeType(fee.type));

// Attaches a client-side row identity to each fee. Fee ids come from the
// server, so rows need their own stable handle for React keys, DOM ids and
// draft lookups.
// A fee already on screen keeps its rowKey, so a re-seed doesn't remount every row.
const withRowKeys = (
  fees: Fee[],
  previous: FeeWithUIState[] = []
): FeeWithUIState[] => {
  const rowKeyById = new Map<string, string>();
  previous.forEach(fee => {
    if (fee.id) rowKeyById.set(fee.id, fee.rowKey);
  });
  return fees.map(fee => {
    const reused = fee.id ? rowKeyById.get(fee.id) : undefined;
    if (fee.id && reused) rowKeyById.delete(fee.id);
    return { ...fee, rowKey: reused ?? crypto.randomUUID() };
  });
};

// Maps draft rows to the wire shape, dropping the UI-only rowKey and trimming
// names. An explicit allowlist rather than a rest-spread so that any UI-only
// field added later cannot leak either — TypeScript won't catch it, since
// FeeWithUIState is assignable to Fee. Covered by FeeManager.save-payload.spec.
// `id` is undefined for a row that has never been saved; JSON.stringify drops
// it, so the server assigns the id.
export const toSavePayload = (fees: FeeWithUIState[]): Fee[] =>
  fees.map(fee => ({
    id: fee.id,
    name: fee.name.trim(),
    // A blank amount input parses to null; it stores as a zero-dollar fee.
    amount: fee.amount ?? 0,
    removed: fee.removed,
    type: fee.type
  }));

export const useFeeState = (initialFees: Fee[] = []) => {
  const [serverFees, setServerFees] = useState<FeeWithUIState[]>(() =>
    withRowKeys(initialFees)
  );
  const [draftFees, setDraftFees] = useState<FeeWithUIState[]>(serverFees);
  const serverFeesRef = useRef(serverFees);
  serverFeesRef.current = serverFees;
  const draftFeesRef = useRef(draftFees);
  draftFeesRef.current = draftFees;
  const [showRemoved, setShowRemoved] = useState(false);
  // Per-row in-progress amount text, keyed by rowKey. Deliberately sparse: an
  // entry exists only for a row the user has typed in; every other row falls
  // back to its stored amount. Kept out of draftFees so it never reaches the
  // isDirty comparison or the save payload.
  const [amountText, setAmountText] = useState<Record<string, string>>({});

  const isDirty = useMemo(
    () => JSON.stringify(draftFees) !== JSON.stringify(serverFees),
    [draftFees, serverFees]
  );

  const [isNewDirty, isUsedDirty] = useMemo(() => {
    const isTypeDirty = (type: FeeType) =>
      JSON.stringify(draftFees.filter(f => f.type === type)) !==
      JSON.stringify(serverFees.filter(f => f.type === type));
    return [isTypeDirty("NEW"), isTypeDirty("USED")];
  }, [draftFees, serverFees]);

  const activeFees = useMemo(
    () => draftFees.filter(f => !f.removed),
    [draftFees]
  );

  const totalAmountNew = useMemo(
    () =>
      activeFees
        .filter(f => f.type === "NEW")
        .reduce((sum, f) => sum + (f.amount ?? 0), 0),
    [activeFees]
  );

  const totalAmountUsed = useMemo(
    () =>
      activeFees
        .filter(f => f.type === "USED")
        .reduce((sum, f) => sum + (f.amount ?? 0), 0),
    [activeFees]
  );

  const hasInvalidFeeNames = useMemo(
    () => draftFees.some(f => !f.removed && !validateFeeName(f.name)),
    [draftFees]
  );

  const setServerAndDraftFees = useCallback((fees: Fee[]) => {
    // The same array must seed both, or the two would get different rowKeys
    // and isDirty would be permanently true.
    const keyed = withRowKeys(withKnownTypes(fees), serverFeesRef.current);
    setDraftFees(keyed);
    setServerFees(keyed);
    setAmountText({});
  }, []);

  // Re-seeds one vehicle type from the server, leaving the other type's draft untouched.
  const setServerAndDraftFeesOfType = useCallback(
    (type: FeeType, fees: Fee[]) => {
      const keyed = withRowKeys(
        fees.filter(f => f.type === type),
        serverFeesRef.current
      );
      const replacedRowKeys = new Set(
        draftFeesRef.current.filter(f => f.type === type).map(f => f.rowKey)
      );
      setServerFees(prev => [...prev.filter(f => f.type !== type), ...keyed]);
      setDraftFees(prev => [...prev.filter(f => f.type !== type), ...keyed]);
      setAmountText(prev =>
        Object.fromEntries(
          Object.entries(prev).filter(
            ([rowKey]) => !replacedRowKeys.has(rowKey)
          )
        )
      );
    },
    []
  );

  const handleCancel = useCallback(() => {
    setDraftFees(serverFees);
    setAmountText({});
  }, [serverFees]);

  const toggleShowRemoved = useCallback(() => {
    setShowRemoved(prev => !prev);
  }, []);

  const softDelete = useCallback((rowKeys: string[]) => {
    const keySet = new Set(rowKeys);
    setDraftFees(prevFees =>
      prevFees.map(fee =>
        keySet.has(fee.rowKey) ? { ...fee, removed: true } : fee
      )
    );
  }, []);

  const restore = useCallback((rowKeys: string[]) => {
    const keySet = new Set(rowKeys);
    setDraftFees(prevFees =>
      prevFees.map(fee =>
        keySet.has(fee.rowKey) ? { ...fee, removed: false } : fee
      )
    );
  }, []);

  const updateDraftFee = useCallback(
    (
      rowKey: string,
      updates: Partial<Pick<Fee, "name" | "amount" | "type">>
    ) => {
      setDraftFees(prevFees =>
        prevFees.map(fee =>
          fee.rowKey === rowKey ? { ...fee, ...updates } : fee
        )
      );
    },
    []
  );

  const addDraftFee = useCallback((fee: FeeWithUIState) => {
    setDraftFees(prevFees => [...prevFees, fee]);
  }, []);

  // Drops a row outright, for a fee that was never saved. A soft delete would
  // leave an empty {name:"", removed:true} row in the draft, which then ships
  // to the API and reappears under Deleted Fees.
  const removeDraftFee = useCallback((rowKey: string) => {
    setDraftFees(prevFees => prevFees.filter(fee => fee.rowKey !== rowKey));
    setAmountText(prev => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  }, []);

  const setFeeAmountText = useCallback((rowKey: string, text: string) => {
    setAmountText(prev => ({ ...prev, [rowKey]: text }));
  }, []);

  return {
    serverFees,
    draftFees,
    isDirty,
    isNewDirty,
    isUsedDirty,
    showRemoved,
    totalAmountNew,
    totalAmountUsed,
    hasInvalidFeeNames,
    amountText,
    setServerAndDraftFees,
    setServerAndDraftFeesOfType,
    handleCancel,
    toggleShowRemoved,
    softDelete,
    restore,
    updateDraftFee,
    addDraftFee,
    removeDraftFee,
    setFeeAmountText
  };
};
